// Loads the world political map (country borders) and joins it to our
// `countries` reference table by ISO 3166-1 numeric code.
//
// Data source: the `world-atlas` npm package (Natural Earth data packaged
// as TopoJSON, the same dataset used in most react-simple-maps examples).
// This file is resolved from node_modules at BUILD time by Vite - nothing
// is fetched from a third-party CDN at runtime, so the map keeps working
// even if some external CDN is unreachable.
//
// IMPORTANT LIMITATION (documented in the SQL migration and the README
// too): Natural Earth's low/medium-resolution country layer assigns id
// "-99" to a handful of disputed/unrecognized territories that have no
// official ISO 3166-1 numeric code (e.g. Kosovo, Somaliland, Northern
// Cyprus). Those features cannot be joined to any row in `countries` and
// are rendered in a fixed muted color, non-interactively, by WorldMap.tsx -
// this is what the spec's own "kolikor to dopuščajo uporabljeni geografski
// podatki" (as far as the geographic data used allows) anticipates.
import { feature } from "topojson-client";
import type { FeatureCollection, Geometry } from "geojson";
// world-atlas itself ships no TypeScript types, but the `?url` suffix below
// is covered by Vite's own ambient `declare module '*?url'` (see
// vite-env.d.ts's `/// <reference types="vite/client" />`), which gives us
// the built asset URL so Vite bundles the file without inlining ~200KB of
// JSON into the main chunk - no `@ts-expect-error` needed for this import.
import worldTopoUrl from "world-atlas/countries-50m.json?url";

export interface CountryFeatureProperties {
  name: string; // Natural Earth's own English name, display fallback only
}

export type CountryFeatureCollection = FeatureCollection<Geometry, CountryFeatureProperties>;

let cachedFeatures: CountryFeatureCollection | null = null;
let inFlight: Promise<CountryFeatureCollection> | null = null;

export async function loadWorldFeatures(): Promise<CountryFeatureCollection> {
  if (cachedFeatures) return cachedFeatures;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    const res = await fetch(worldTopoUrl);
    if (!res.ok) throw new Error(`failed to load world map data: ${res.status}`);
    const topology = await res.json();
    const object = topology.objects.countries;
    const collection = feature(topology, object) as unknown as CountryFeatureCollection;
    cachedFeatures = collection;
    return collection;
  })();

  return inFlight;
}

/**
 * world-atlas feature ids are ISO 3166-1 numeric codes as strings (e.g.
 * "004", sometimes without leading zeros depending on the package version).
 * "-99" marks a territory with no official numeric code - see the module
 * doc comment above.
 */
export function featureIdToCountryNumeric(featureId: string | number | undefined): number | null {
  if (featureId === undefined || featureId === null) return null;
  const n = Number(featureId);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}
